import React, {useState, useCallback, useEffect} from 'react';
import {i18n} from '@netx/core/i18n';

import {Menu, MenuItem, type MenuProps} from '@mui/material';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {Theme} from '@netx/core-components/Theme';

import Button, {type ButtonProps} from '@netx/core-components/Button';
import {Icon, faCaretDown, faCheck} from '@netx/core-icons';

import {PERMISSION_TYPE_LABEL} from '../../locales';
import type {PermissionMode} from '../../types';
import {PERMISSION_MODE, PERMISSION_TYPE} from '../../constants';
import useWhenChangedEffect from '@netx/core-components/hooks/useWhenChangedEffect';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';

export type PermissionItemMenuProps = {
	value: PermissionMode;
	onChange(value: PermissionMode): void;
	onRemove(): void;
	onClose?: () => void;
	onOpen?: () => void;
	open?: boolean;

	ButtonProps?: Partial<ButtonProps>;
	MenuProps?: Partial<MenuProps>;
};

const styles = (theme: Theme) =>
	createStyles({
		dropdownIcon: {
			marginLeft: 8,
		},
		// TODO: make a menu component that handles this checkmark stuff
		activeIcon: {
			width: 32,
			color: theme.palette.secondary.main,
			position: 'absolute',
			right: 8,
			top: 0,
			bottom: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		menu: {
			// TODO: make a decision on our menus - they get bigger at smaller screens
			// this layout works better with the sizing from that breakpoint
			'& .MuiMenuItem-root': {
				minHeight: '48px !important',
			},
			// TODO: something funky in the menu is preventing this from happening
			// when using arrow keys to navigate menu items
			'& li:focus': {
				backgroundColor: theme.palette.action.hover,
			},
		},
		permissionModeItem: {
			paddingRight: 48,
		},
	});

export const PermissionItemMenu = ({
	classes,
	value,
	onChange,
	onClose,
	onOpen,
	onRemove,
	open,
	ButtonProps,
	MenuProps,
}: PermissionItemMenuProps & WithStyles<typeof styles>) => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const internalButtonRef = useSharedRef<HTMLButtonElement | null>(null, [
		ButtonProps?.ref,
	]);

	useEffect(() => {
		open ? setAnchorEl(internalButtonRef.current) : setAnchorEl(null);
	}, [open]);

	useWhenChangedEffect(
		anchorEl,
		useCallback(
			(anchorEl: HTMLButtonElement | null) => {
				anchorEl ? onOpen?.() : onClose?.();
			},
			[onOpen, onClose],
		),
	);

	const handleOpenClick = useCallback(
		(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
			setAnchorEl(
				(event as React.MouseEvent<HTMLButtonElement, MouseEvent>)
					.currentTarget,
			),
		[onOpen],
	);

	const handleClose = useCallback(() => setAnchorEl(null), []);

	const selectPermission = useCallback(
		(perm: PermissionMode) => () => {
			onChange(perm);
			setAnchorEl(null);
		},
		[onChange],
	);

	const handleRemove = useCallback(() => {
		setAnchorEl(null);
		onRemove();
	}, [onRemove]);

	const modeEntries = Object.entries(PERMISSION_MODE);
	const modeCount = modeEntries.length;

	return (
		<>
			<Button
				aria-haspopup="true"
				aria-label="permission menu"
				onClick={handleOpenClick}
				variant="text"
				{...ButtonProps}
				ref={internalButtonRef}
			>
				<span>
					{
						PERMISSION_TYPE_LABEL[
							value === 'write' ? PERMISSION_TYPE.WRITE : PERMISSION_TYPE.READ
						]
					}
				</span>
				<span className={classes.dropdownIcon}>
					<Icon icon={faCaretDown} />
				</span>
			</Button>
			{/** TODO: Make/adjust menu component to handle this checkmark active stuff */}
			<Menu
				id="simple-menu"
				className={classes.menu}
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				{...MenuProps}
			>
				{modeEntries.map(([key, mode], index) => (
					<MenuItem
						key={mode}
						selected={value === mode}
						onClick={selectPermission(mode)}
						divider={modeCount - 1 === index}
						className={classes.permissionModeItem}
						dense={false}
					>
						<>
							{PERMISSION_TYPE_LABEL[key as keyof typeof PERMISSION_TYPE_LABEL]}
							<span className={classes.activeIcon}>
								{value === mode ? <Icon icon={faCheck} /> : null}
							</span>
						</>
					</MenuItem>
				))}
				<MenuItem key={'remove'} onClick={handleRemove}>
					{i18n.t('i18n.remove')}
				</MenuItem>
			</Menu>
		</>
	);
};

export default withStyles(styles)(PermissionItemMenu) as (
	props: Omit<PermissionItemMenuProps, 'classes'>,
) => JSX.Element;
